import { useState, useEffect, useCallback } from "react";

// ✅ Tous les imports en haut
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import DevisModel from "../models/DevisModel";

// ✅ Base URL API
export const base_url = "http://localhost:8000/api/v1/";


export function useGetDevis(initialStatut = "Tous", initialDateRange = null, customToken = null) {
  const [devis, setDevis] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({
    total: 0,
    per_page: 6,
    current_page: 1,
    last_page: 1,
  });
  const [statut, setStatut] = useState(initialStatut);
  const [dateRange, setDateRange] = useState(initialDateRange || { startDate: null, endDate: null });

  const reduxToken = useSelector((state) => state.auth.token);
  const token = customToken || reduxToken; // ✅ si pas de token fourni, on prend celui du redux

  const navigate = useNavigate();

  const fetchDevis = useCallback(
    async (customPage = 1, filterStatut = statut, range = dateRange, client = "", numeroDevis = "") => {
      if (!token) {
        navigate("/login");
        return;
      }

      setLoading(true);
      try {
        const url = new URL(`${base_url}devis`);
        url.searchParams.append("page", customPage);
        url.searchParams.append("per_page", pagination.per_page);
        url.searchParams.append("statut", filterStatut || "Tous");

        if (range.startDate) url.searchParams.append("date_debut_emission", range.startDate.toISOString().split("T")[0]);
        if (range.endDate) url.searchParams.append("date_fin_emission", range.endDate.toISOString().split("T")[0]);
        if (client.trim() !== "") url.searchParams.append("client", client.trim());
        if (numeroDevis.trim() !== "") url.searchParams.append("numero_devis", numeroDevis.trim());

        const response = await fetch(url, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) throw new Error("Erreur lors de la récupération des devis");

        const data = await response.json();
        const devisFormatés = data.devis.map((item) => new DevisModel(item));

        setDevis(devisFormatés);
        setPagination({
          total: data.total,
          per_page: data.per_page,
          current_page: data.current_page,
          last_page: Math.ceil(data.total / data.per_page),
        });
      } catch (error) {
        console.error("Erreur:", error);
        setDevis([]);
      } finally {
        setLoading(false);
      }
    },
    [token, navigate, pagination.per_page, statut, dateRange]
  );

  useEffect(() => {
    fetchDevis(page, statut, dateRange);
  }, [fetchDevis, page, statut, dateRange]);

  return {
    devis,
    loading,
    pagination,
    page,
    setPage,
    fetchDevis,
    setDevis,
    setLoading,
    setPagination,
    statut,
    setStatut,
    dateRange,
    setDateRange,
  };
}
export async function getDevis(id, customToken = null) {
  const token = customToken || localStorage.getItem("authTokenUtilisateur"); // ✅ token parent ou localStorage
  if (!token) throw new Error("Utilisateur non authentifié");

  if (!id || isNaN(parseInt(id))) throw new Error("ID de devis invalide");

  const response = await fetch(`${base_url}devis/${id}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  let data;
  try {
    data = await response.json();
  } catch (err) {
    console.error("Erreur parsing JSON :", err);
    throw new Error("Le serveur a renvoyé une réponse invalide");
  }

  if (!response.ok) throw new Error(data.message || "Erreur lors de la récupération du devis");

  const devisInstance = new DevisModel(data.devis || data);
  return devisInstance;
}
// ✅ Créer une facture à partir d’un devis
export async function createFactureFromDevis(devisId) {
  const token = localStorage.getItem("authTokenUtilisateur");
  if (!token) throw new Error("Utilisateur non authentifié");

  if (!devisId || isNaN(parseInt(devisId))) throw new Error("ID de devis invalide");

  const response = await fetch(`${base_url}factures/from-devis/${devisId}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  let data;
  try {
    data = await response.json();
  } catch (err) {
    console.error("Erreur parsing JSON :", err);
    throw new Error("Le serveur a renvoyé une réponse invalide");
  }

  if (!response.ok) {
    // Exemple : déjà transféré
    throw new Error(data.message || "Erreur lors du transfert du devis en facture");
  }

  return data;
  /*
    Exemple retour succès :
    {
      success: true,
      message: "Facture créée avec succès à partir du devis N° D2025-001",
      facture: {...}
    }

    Exemple retour erreur si déjà transféré :
    {
      success: false,
      message: "Ce devis a déjà été transféré vers la facture N° F2025-001",
      facture_id: 12,
      numero_facture: "F2025-001"
    }
  */
}


// services/DevisService.js
export async function addDevis(devisData) {
  const token = localStorage.getItem("authTokenUtilisateur"); // token correct
  if (!token) throw new Error("Utilisateur non authentifié");

  // Préparer le payload pour le backend
  const payload = {
    client_id: devisData.client_id,
    numero: devisData.numero || "",
    date_emission: devisData.date_emission,
    date_expiration: devisData.date_expiration || null,
    statut: devisData.statut || "brouillon",
    titre: devisData.titre?.trim() || null,
    notes_client: devisData.notes_client?.trim() || null,
    notes_internes: devisData.notes_internes?.trim() || null,
    sous_total_centimes: devisData.sous_total_centimes || 0,
    remise_centimes: devisData.remise_centimes || 0,
    tva_centimes: devisData.tva_centimes || 0,
    total_centimes: devisData.total_centimes || 0,
    devise: devisData.devise || "EUR",
    lignes: devisData.lignes.map((ligne) => ({
      designation: ligne.designation?.trim() || "",
      quantite: parseFloat(ligne.quantite) || 1,
      unite: ligne.unite?.trim() || null,
      prix_unitaire_centimes: parseInt(ligne.prix_unitaire_centimes) || 0,
      taux_tva: parseFloat(ligne.taux_tva) || 0,
      taux_remise: parseFloat(ligne.taux_remise) || 0,
      total_ligne_centimes: parseInt(ligne.total_ligne_centimes) || 0,
    })),
    // Ajout des secteurs sélectionnés
    secteurs_batiment: devisData.secteurs_batiment || [], // tableau d'id
    secteurs_autres: devisData.secteurs_autres || [],     // tableau d'id
  };

  const response = await fetch(`${base_url}devis`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });

  let data;
  try {
    data = await response.json();
  } catch (err) {
    console.error("Erreur parsing JSON :", err);
    throw new Error("Le serveur a renvoyé une réponse invalide");
  }

  if (!response.ok) throw new Error(data.message || "Erreur lors de l'ajout du devis");

  return data; // retour du message succès ou devis créé
}


// Mettre à jour un devis existant
export async function updateDevis(id, devisData) {
  const token = localStorage.getItem("authTokenUtilisateur");
  if (!token) throw new Error("Utilisateur non authentifié");

  if (!id || isNaN(parseInt(id))) throw new Error("ID de devis invalide");

  const response = await fetch(`${base_url}devis/${id}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(devisData),
  });

  let data;
  try {
    data = await response.json();
  } catch (err) {
    console.error("Erreur parsing JSON :", err);
    throw new Error("Le serveur a renvoyé une réponse invalide");
  }

  if (!response.ok) throw new Error(data.message || "Erreur lors de la mise à jour du devis");

  return data; // { success: true, message: "...", devis: {...} }
}


// Fonction pour supprimer un client
export async function deleteDevis(devisId) {
  const token = localStorage.getItem("authTokenUtilisateur");
  if (!token) throw new Error("Utilisateur non authentifié");

  const response = await fetch(`${base_url}devis/${devisId}`, {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  let data;
  try {
    data = await response.json();
  } catch (err) {
    console.error("Erreur parsing JSON :", err);
    throw new Error("Le serveur a renvoyé une réponse invalide");
  }

  if (!response.ok) throw new Error(data.message || "Erreur lors de la suppression du devis");

  return data; // renvoie { success: true, message: 'Devis supprimé avec succès' }



}





